// src/follower/components/GuardrailsPanel.jsx
// Phase 4 — Pause/Resume wired to EC2 dedicated routes. Save prefs hits Lambda.
// Emergency Close wired to EC2 POST /follower/liquidate.
// Section B of myTrades page.

import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

// ─── API helpers ──────────────────────────────────────────────────────────────

// Lambda routes — Bearer token + api key
function lambdaHeaders() {
    const token = sessionStorage.getItem('token');
    return {
        'x-api-key':     config.API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type':  'application/json',
    };
}

// EC2 routes — matches admin.js pattern: x-api-key + raw authorizationToken (no Bearer prefix)
function ec2Headers() {
    const token = sessionStorage.getItem('token');
    return {
        'x-api-key':        config.API_KEY,
        'authorizationToken': token,
        'Content-Type':     'application/json',
    };
}

async function apiSavePrefs(payload) {
    await axios.post(config.API_BASE_URL + '/follower/prefs', payload, { headers: lambdaHeaders() });
}

async function apiPause() {
    await axios.post(config.API_SOCKET_URL + '/follower/pause',
        { reason: 'MANUAL' },
        { headers: ec2Headers() }
    );
}

async function apiResume() {
    await axios.post(config.API_SOCKET_URL + '/follower/resume',
        {},
        { headers: ec2Headers() }
    );
}

async function apiLiquidate() {
    await axios.post(config.API_SOCKET_URL + '/follower/liquidate', {}, { headers: ec2Headers() });
}

const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    border2:"#243548",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";



const Label = ({ children }) => (
    <label style={{
    display: "block", fontFamily: FONT, fontSize: 9, fontWeight: 500,
    letterSpacing: "1px", textTransform: "uppercase", color: C.meta, marginBottom: 6,
  }}>
        {children}
    </label>
);

const NumberInput = ({ value, onChange, disabled, min, max, step = 1 }) => (
    <input
        type="number"
        value={value}
        onChange={onChange}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        style={{
      width: "100%", background: C.bg, border: `1px solid ${C.border}`,
      borderRadius: 4, color: C.text, fontFamily: FONT, fontSize: 12,
      padding: "9px 12px", outline: "none", letterSpacing: ".3px",
      boxSizing: "border-box", transition: "border-color .15s",
      opacity: disabled ? 0.45 : 1, MozAppearance: "textfield",
    }}
        onFocus={e => { if (!disabled) e.target.style.borderColor = C.cyan; }}
        onBlur={e  => { e.target.style.borderColor = C.border; }}
    />
);

const ActionBtn = ({ label, color, onClick, disabled, loading }) => (
    <button
        onClick={onClick}
        disabled={disabled || loading}
        style={{
      fontFamily: FONT, fontSize: 9, fontWeight: 500, letterSpacing: "1px",
      textTransform: "uppercase", padding: "8px 18px", borderRadius: 4,
      border: `1px solid ${color}40`, background: color + "12",
      color: disabled ? C.meta : color,
      cursor: disabled || loading ? "not-allowed" : "pointer",
      opacity: disabled || loading ? 0.5 : 1,
      transition: "background .15s, border-color .15s", whiteSpace: "nowrap",
    }}
    >
        {loading ? "..." : label}
    </button>
);

function ConfirmDialog({ onConfirm, onCancel }) {
    return (
        <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(4,8,18,0.88)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
            <div style={{
        width: "100%", maxWidth: 400, background: C.bg1,
        border: `1px solid ${C.red}40`, borderRadius: 10, overflow: "hidden",
      }}>
                <div style={{
          padding: "16px 24px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.red, boxShadow: `0 0 8px ${C.red}`, flexShrink: 0 }} />
          <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
            Emergency Close All
          </span>
                </div>
                <div style={{ padding: "20px 24px" }}>
                    <p style={{ fontFamily: FONT, fontSize: 11, color: C.body, lineHeight: 1.7, margin: "0 0 8px" }}>
                        This will close <strong style={{ color: C.text }}>all open positions</strong> immediately.
                    </p>
                    <p style={{ fontFamily: FONT, fontSize: 10, color: C.meta, lineHeight: 1.6, margin: "0 0 24px" }}>
                        If the market is closed or you need an emergency exit outside trading hours,
                        use your Alpaca dashboard directly.
                    </p>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button
                            onClick={onCancel}
                            style={{
                flex: 1, fontFamily: FONT, fontSize: 9, letterSpacing: "1px",
                padding: "9px", borderRadius: 4, border: `1px solid ${C.border}`,
                background: "transparent", color: C.meta, cursor: "pointer",
              }}
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={onConfirm}
                            style={{
                flex: 1, fontFamily: FONT, fontSize: 9, fontWeight: 500,
                letterSpacing: "1px", padding: "9px", borderRadius: 4,
                border: `1px solid ${C.red}50`, background: C.red + "18",
                color: C.red, cursor: "pointer",
              }}
                        >
                            CLOSE ALL POSITIONS
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function GuardrailsPanel({ prefs, onPrefsChange, onEmergencyClose, marketOpen, isDemo }) {
    const [maxContracts,   setMaxContracts]   = useState(prefs.max_contracts);
    const [maxDailyLoss,   setMaxDailyLoss]   = useState(prefs.max_daily_loss);
    const [maxDailyProfit, setMaxDailyProfit] = useState(prefs.max_daily_profit);
    const [savingPrefs,    setSavingPrefs]    = useState(false);
    const [savingPause,    setSavingPause]    = useState(false);
    const [liquidating,    setLiquidating]    = useState(false);
    const [showConfirm,    setShowConfirm]    = useState(false);
    const [saveSuccess,    setSaveSuccess]    = useState(false);
    const [liquidateError, setLiquidateError] = useState(null);

    const isPaused = prefs.is_paused || !!(prefs.pause_reason);

    useEffect(() => {
        setMaxContracts(prefs.max_contracts);
        setMaxDailyLoss(prefs.max_daily_loss);
        setMaxDailyProfit(prefs.max_daily_profit);
    }, [prefs]);

    const isDirty =
        maxContracts   !== prefs.max_contracts   ||
        maxDailyLoss   !== prefs.max_daily_loss  ||
        maxDailyProfit !== prefs.max_daily_profit;

    const handleSave = async () => {
        if (!isDirty || savingPrefs) return;
        setSavingPrefs(true);
        try {
            await apiSavePrefs({ max_contracts: maxContracts, max_daily_loss: maxDailyLoss, max_daily_profit: maxDailyProfit });
            onPrefsChange({ ...prefs, max_contracts: maxContracts, max_daily_loss: maxDailyLoss, max_daily_profit: maxDailyProfit });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (err) {
            console.error('Save prefs error:', err);
        } finally {
            setSavingPrefs(false);
        }
    };

    const handlePause = async () => {
        if (savingPause) return;
        setSavingPause(true);
        try {
            await apiPause();
            onPrefsChange({ ...prefs, is_paused: true, pause_reason: "MANUAL" });
        } catch (err) {
            console.error('Pause error:', err);
        } finally {
            setSavingPause(false);
        }
    };

    const handleResume = async () => {
        if (savingPause) return;
        setSavingPause(true);
        try {
            await apiResume();
            onPrefsChange({ ...prefs, is_paused: false, pause_reason: "" });
        } catch (err) {
            console.error('Resume error:', err);
        } finally {
            setSavingPause(false);
        }
    };

    const handleConfirmLiquidate = async () => {
        setShowConfirm(false);
        setLiquidating(true);
        setLiquidateError(null);
        try {
            await apiLiquidate();
            onEmergencyClose?.();
        } catch (err) {
            if (err?.response?.status === 403) {
                setLiquidateError("Market is closed. Use your Alpaca dashboard.");
            } else {
                console.error('Liquidate error:', err);
            }
        } finally {
            setLiquidating(false);
        }
    };

    return (
        <>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>

        {showConfirm && (
        <ConfirmDialog
            onConfirm={handleConfirmLiquidate}
            onCancel={() => setShowConfirm(false)}
        />
            )}

        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>

            {/* Header */}
            <div style={{
          padding: "14px 24px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 3, height: 16, borderRadius: 2, background: `linear-gradient(180deg, ${C.cyan}, ${C.cyan}40)` }} />
            <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
              Guardrails
            </span>
                </div>
                {isPaused && prefs.pause_reason && (
                <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "4px 12px", borderRadius: 4,
              border: `1px solid ${C.amber}30`, background: C.amber + "0d",
            }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.amber, flexShrink: 0 }} />
              <span style={{ fontFamily: FONT, fontSize: 9, color: C.amber, letterSpacing: ".5px" }}>
                {prefs.pause_reason === "MANUAL"             && "Manually paused"}
                  {prefs.pause_reason === "MAX_LOSS_BREACHED"  && "Max loss reached"}
                  {prefs.pause_reason === "MAX_PROFIT_REACHED" && "Max profit reached"}
              </span>
                </div>
                    )}
            </div>

            {/* Body */}
            <div style={{ padding: "24px" }}>

                {/* Demo banner */}
                {isDemo && (
                <div style={{
                        background:   C.amber + "0d",
                        border:       `1px solid ${C.amber}25`,
                        borderRadius: 6,
                        padding:      "8px 14px",
                        marginBottom: 20,
                        display:      "flex",
                        alignItems:   "center",
                        gap:          8,
                    }}>
                    <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.amber, flexShrink: 0 }} />
                        <span style={{ fontFamily: FONT, fontSize: 9, color: C.amber, letterSpacing: ".3px" }}>
                            View only — actions disabled in demo mode
                        </span>
                </div>
                    )}

                {/* Inputs */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
                    <div>
                        <Label>Max Contracts</Label>
                        <NumberInput value={maxContracts} onChange={e => setMaxContracts(Number(e.target.value))} disabled={savingPrefs || isDemo} min={1} max={5} />
                        <div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, marginTop: 4, letterSpacing: ".3px" }}>1 – 5 contracts per trade</div>
                    </div>
                    <div>
                        <Label>Max Daily Loss</Label>
                        <NumberInput value={maxDailyLoss} onChange={e => setMaxDailyLoss(Number(e.target.value))} disabled={savingPrefs || isDemo} max={0} step={50} />
                        <div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, marginTop: 4, letterSpacing: ".3px" }}>Negative value, e.g. −500</div>
                    </div>
                    <div>
                        <Label>Max Daily Profit</Label>
                        <NumberInput value={maxDailyProfit} onChange={e => setMaxDailyProfit(Number(e.target.value))} disabled={savingPrefs || isDemo} min={0} step={50} />
                        <div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, marginTop: 4, letterSpacing: ".3px" }}>Positive value, e.g. 1000</div>
                    </div>
                </div>

                {/* Controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <ActionBtn
                        label={saveSuccess ? "SAVED ✓" : "SAVE GUARDRAILS"}
                        color={saveSuccess ? C.green : C.cyan}
                        onClick={handleSave}
                        disabled={!isDirty || saveSuccess || isDemo}
                        loading={savingPrefs}
                    />
                    {isPaused ? (
                    <ActionBtn label="RESUME COPYING" color={C.green} onClick={handleResume} loading={savingPause} disabled={isDemo} />
                        ) : (
                    <ActionBtn label="PAUSE COPYING" color={C.amber} onClick={handlePause} loading={savingPause} disabled={isDemo} />
                        )}
                    {marketOpen && (
                    <button
                        onClick={() => !liquidating && !isDemo && setShowConfirm(true)}
                        disabled={liquidating || isDemo}
                        style={{
                  marginLeft: "auto", fontFamily: FONT, fontSize: 9, fontWeight: 500,
                  letterSpacing: "1px", textTransform: "uppercase", padding: "8px 18px",
                  borderRadius: 4, border: `1px solid ${C.red}40`, background: C.red + "12",
                  color: liquidating || isDemo ? C.meta : C.red,
                  cursor: liquidating || isDemo ? "not-allowed" : "pointer",
                  opacity: liquidating || isDemo ? 0.5 : 1, transition: "background .15s",
                }}
                    >
                        {liquidating ? "CLOSING..." : "⚠ EMERGENCY CLOSE ALL"}
                    </button>
                        )}
                </div>

                {/* Liquidate error message */}
                {liquidateError && (
                <div style={{
                        marginTop: 12, background: C.amber + "0d",
                        border: `1px solid ${C.amber}30`, borderRadius: 6,
                        padding: "8px 14px", display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: 10,
                    }}>
                    <span style={{ fontFamily: FONT, fontSize: 9, color: C.amber, letterSpacing: ".3px" }}>
                        {liquidateError}
                    </span>
                    <button onClick={() => setLiquidateError(null)} style={{ background: "transparent", border: "none", color: C.meta, cursor: "pointer", fontFamily: FONT, fontSize: 10 }}>✕</button>
                </div>
                    )}

            </div>
        </div>
        </>
    );
}